import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { trpc } from './anschluss'

/**
 * **Die Daten des Notizbuchs in der Oberfläche** – Abfragen und optimistisches Speichern
 * (Leitplanke 1: keine Standardaktion wartet auf den Server).
 */

export const SCHLUESSEL = {
  stand: ['notizbuch', 'stand'] as const,
  meine: ['notizbuch', 'meine'] as const,
  liste: (nb: string) => ['notizbuch', 'liste', nb] as const,
  notiz: (nb: string, id: string) => ['notizbuch', 'notiz', nb, id] as const,
}

type Liste = Awaited<ReturnType<typeof trpc.notizbuch.notiz.liste.query>>
export type NotizKurz = Liste['notizen'][number]
export type Mappe = Liste['mappen'][number]
export type Notiz = Awaited<ReturnType<typeof trpc.notizbuch.notiz.lesen.query>>
export type MeinNotizbuch = Awaited<ReturnType<typeof trpc.notizbuch.meine.query>>[number]

export function useStand() {
  return useQuery({ queryKey: SCHLUESSEL.stand, queryFn: () => trpc.notizbuch.stand.query() })
}

export function useMeine(aktiv = true) {
  return useQuery({
    queryKey: SCHLUESSEL.meine,
    queryFn: () => trpc.notizbuch.meine.query(),
    enabled: aktiv,
  })
}

/** Listen mehrerer Notizbücher auf einmal – die Leiste zeigt immer die Kombination. */
export function useListen(notizbuchIds: readonly string[]) {
  return useQueries({
    queries: notizbuchIds.map((nb) => ({
      queryKey: SCHLUESSEL.liste(nb),
      queryFn: () => trpc.notizbuch.notiz.liste.query({ notizbuchId: nb }),
      staleTime: 30_000,
    })),
  })
}

export function useNotiz(nb: string, id: string, aktiv = true) {
  return useQuery({
    queryKey: SCHLUESSEL.notiz(nb, id),
    queryFn: () => trpc.notizbuch.notiz.lesen.query({ notizbuchId: nb, id }),
    enabled: aktiv,
    retry: false,
  })
}

export interface SpeichernEingabe {
  notizbuchId: string
  id: string
  titel: string
  inhalt: string
  art?: string
  mappe?: string | null
  datum?: string
  tags?: string[]
  felder?: Record<string, string>
  termine?: { titel: string; beginn: string; ende: string }[]
  zeitmarken?: (string | null)[]
}

/**
 * Speichern – die Liste wird sofort ergänzt, der Server bestätigt hinterher. Bei einem Fehler
 * rollt die Liste zurück; der Text im Blatt bleibt stehen und wird beim nächsten Speichern
 * erneut geschickt.
 */
export function useSpeichern() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (e: SpeichernEingabe) => trpc.notizbuch.notiz.speichern.mutate(e),
    onMutate: async (e) => {
      const schluessel = SCHLUESSEL.liste(e.notizbuchId)
      await qc.cancelQueries({ queryKey: schluessel })
      const vorher = qc.getQueryData<Liste>(schluessel)
      if (vorher) {
        const alt = vorher.notizen.find((n) => n.id === e.id)
        const kurz: NotizKurz = {
          id: e.id,
          titel: e.titel,
          art: e.art ?? alt?.art ?? 'notiz',
          datum: e.datum ?? alt?.datum ?? new Date().toISOString(),
          mappe: e.mappe === undefined ? (alt?.mappe ?? null) : e.mappe,
          tags: alt?.tags ?? [],
          quelle: alt?.quelle ?? 'work',
          termin: e.termine?.[0]
            ? { titel: e.termine[0].titel, beginn: e.termine[0].beginn }
            : (alt?.termin ?? null),
          ki: alt?.ki ?? null,
          auszug: e.inhalt
            .replace(/[#>*_`[\]()!-]+/g, ' ')
            .replace(/\s+/g, ' ')
            .trim()
            .slice(0, 160),
        }
        qc.setQueryData<Liste>(schluessel, {
          ...vorher,
          notizen: [kurz, ...vorher.notizen.filter((n) => n.id !== e.id)],
        })
      }
      return { vorher }
    },
    onError: (_f, e, kontext) => {
      if (kontext?.vorher) qc.setQueryData(SCHLUESSEL.liste(e.notizbuchId), kontext.vorher)
    },
    onSuccess: (antwort, e) => {
      qc.setQueryData(SCHLUESSEL.liste(e.notizbuchId), (alt: Liste | undefined) =>
        alt
          ? { ...alt, notizen: [antwort.kurz, ...alt.notizen.filter((n) => n.id !== e.id)] }
          : alt,
      )
      void qc.invalidateQueries({ queryKey: SCHLUESSEL.notiz(e.notizbuchId, e.id) })
    },
  })
}

export function useLoeschen() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (e: { notizbuchId: string; id: string }) => trpc.notizbuch.notiz.loeschen.mutate(e),
    onMutate: (e) => {
      qc.setQueryData(SCHLUESSEL.liste(e.notizbuchId), (alt: Liste | undefined) =>
        alt ? { ...alt, notizen: alt.notizen.filter((n) => n.id !== e.id) } : alt,
      )
    },
    onSettled: (_a, _f, e) => qc.invalidateQueries({ queryKey: SCHLUESSEL.liste(e.notizbuchId) }),
  })
}

export function useMappenSpeichern() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (e: { notizbuchId: string; mappen: Mappe[] }) =>
      trpc.notizbuch.notiz.mappenSpeichern.mutate(e),
    onMutate: (e) => {
      qc.setQueryData(SCHLUESSEL.liste(e.notizbuchId), (alt: Liste | undefined) =>
        alt ? { ...alt, mappen: e.mappen } : alt,
      )
    },
    onSettled: (_a, _f, e) => qc.invalidateQueries({ queryKey: SCHLUESSEL.liste(e.notizbuchId) }),
  })
}

/**
 * **Neu laden** – der Server liest die Notizbücher frisch aus dem Speicher. Nötig, wenn ein Import
 * an Work vorbei geschrieben hat (OneNote, Obsidian): der Server hält den Bestand im Speicher.
 */
export function useNeuLaden() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (ids: readonly string[]) => {
      const r = await Promise.all(
        ids.map((notizbuchId) => trpc.notizbuch.notiz.neuLaden.mutate({ notizbuchId })),
      )
      return r.reduce((summe, x) => summe + x.notizen, 0)
    },
    onSuccess: (_n, ids) => {
      for (const id of ids) void qc.invalidateQueries({ queryKey: SCHLUESSEL.liste(id) })
    },
  })
}

/**
 * **Suche nach Bedeutung** – erst, wenn die Frage eine halbe Sekunde stillsteht; jede Frage ist
 * ein (winziger) Aufruf bei Mistral.
 */
export function useBedeutung(notizbuchIds: readonly string[], frage: string) {
  const [ruhig, setRuhig] = useState('')
  useEffect(() => {
    const uhr = setTimeout(() => setRuhig(frage.trim()), 500)
    return () => clearTimeout(uhr)
  }, [frage])
  return useQuery({
    queryKey: ['notizbuch', 'suche', [...notizbuchIds].sort(), ruhig],
    queryFn: () =>
      trpc.notizbuch.notiz.suche.query({ notizbuchIds: [...notizbuchIds], frage: ruhig }),
    enabled: ruhig.length >= 3 && notizbuchIds.length > 0,
    staleTime: 5 * 60_000,
    // Beim allerersten Suchen baut der Server den Volltext auf – dann gleich noch einmal fragen.
    refetchInterval: (q) => (q.state.data?.volltextLaedt ? 4000 : false),
  })
}

/** Mappen-Vorschläge (über die Vektoren) – für die Zettel ohne Mappe oder einzelne Notizen. */
export function useMappenVorschlaege(nb: string | null, ids?: readonly string[]) {
  return useQuery({
    queryKey: ['notizbuch', 'mappenVorschlaege', nb, ids ? [...ids].sort() : 'zettel'],
    queryFn: () =>
      trpc.notizbuch.notiz.mappenVorschlaege.query({
        notizbuchId: nb!,
        ...(ids ? { ids: [...ids] } : {}),
      }),
    enabled: !!nb,
    staleTime: 5 * 60_000,
  })
}

/** Ablegen: nur die Mappe setzen – sofort in der Liste, der Server bestätigt (Leitplanke 1). */
export function useMappeSetzen() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (e: { notizbuchId: string; id: string; mappe: string | null }) =>
      trpc.notizbuch.notiz.mappeSetzen.mutate(e),
    onMutate: (e) => {
      qc.setQueryData(SCHLUESSEL.liste(e.notizbuchId), (alt: Liste | undefined) =>
        alt
          ? {
              ...alt,
              notizen: alt.notizen.map((n) => (n.id === e.id ? { ...n, mappe: e.mappe } : n)),
            }
          : alt,
      )
    },
    onSettled: (_a, _f, e) => {
      void qc.invalidateQueries({ queryKey: SCHLUESSEL.liste(e.notizbuchId) })
      void qc.invalidateQueries({ queryKey: SCHLUESSEL.notiz(e.notizbuchId, e.id) })
    },
  })
}
